import {
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Persona,
  Spinner,
  Switch,
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Globe20Regular, LinkRegular, PersonRegular, SignOutRegular, WeatherMoon20Regular, WeatherSunny20Regular } from "@fluentui/react-icons";
import { perfMark } from "../perf";
import { useState, type ReactNode } from "react";
import { useAuth } from "@adapt/auth";
import { getRuntimeConfig } from "@adapt/auth";
import { useOptionalAdaptTheme } from "../theme/adaptTheme";

export interface ExternalLink {
  label: string;
  url: string;
}

export interface LanguageOption {
  /** BCP-47 language code, e.g. "en", "tet", "pt" */
  code: string;
  /** Human-readable display label shown in the button and dropdown */
  label: string;
}

const DEFAULT_LANGUAGES: LanguageOption[] = [
  { code: "en",  label: "English"   },
  { code: "tet", label: "Tetum"     },
  { code: "pt",  label: "Português" },
];

/** Default tooling links — match adapt-core portal LinksDropdown defaults. */
const DEFAULT_EXTERNAL_LINKS: ExternalLink[] = [
  { label: "MLflow",       url: "https://adapt-demo-mlflow.wonderfulpebble-34c8fb5b.australiaeast.azurecontainerapps.io" },
  { label: "Evidently",    url: "https://adapt-demo-evidently.wonderfulpebble-34c8fb5b.australiaeast.azurecontainerapps.io" },
  { label: "OpenMetadata", url: "https://adapt-demo-openmetadata.wonderfulpebble-34c8fb5b.australiaeast.azurecontainerapps.io" },
  { label: "Training",     url: "https://share.articulate.com/fAmUvkCi96grjBGm46xO2#/lessons/1wFbQTZLFCENS7nnduYpmQPoWeTkBiO4" },
];

export interface AuthenticatedLayoutProps {
  children: ReactNode;
  appName?: string;
  brand?: ReactNode;
  externalLinks?: ExternalLink[];
  headerActions?: ReactNode;
  loadingFallback?: ReactNode;
  navigation?: ReactNode;
  /** When true the sidebar collapses to icon-only width (56 px). */
  navCollapsed?: boolean;
  showThemeToggle?: boolean;
  themeToggle?: ReactNode;
  unauthenticatedFallback?: ReactNode;
  /** Show a language-selector dropdown in the header. Defaults to false. */
  showLanguageSelector?: boolean;
  /** Override the selectable languages. Defaults to English / Tetum / Português. */
  languages?: LanguageOption[];
  /** Initially-selected language code. Defaults to "en". */
  defaultLanguage?: string;
  /** Called whenever the user picks a different language. */
  onLanguageChange?: (code: string) => void;
}

// ─── Griffel styles matching the legacy ADAPT ClassNames.ts exactly ──────────

const useStyles = makeStyles({
  // Full-viewport fixed wrapper — matches appFullHeightStyles
  appShell: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground3,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },

  // Fixed header — matches appHeader
  header: {
    height: "var(--app-header-height, 48px)",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: "1030",
    padding: 0,
    margin: 0,
    backgroundColor: tokens.colorBrandBackground4Static,
    color: tokens.colorNeutralForegroundInverted2,
    whiteSpace: "nowrap",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "#323e48",
    boxSizing: "content-box",
    display: "grid",
    gridTemplateColumns: "minmax(100px, 288px) 1fr",
    gridTemplateAreas: '"brand actions"',
    gridTemplateRows: "var(--app-header-height, 48px)",
  },

  // Brand area (left side of header)
  headerBrand: {
    gridArea: "brand",
    display: "flex",
    alignItems: "center",
  },

  // App name button in brand area — matches appBrandNameBtn
  brandBtn: {
    height: "100%",
    minWidth: "min-content",
    display: "flex",
    alignItems: "center",
    lineHeight: "1",
    border: "0 solid transparent",
    padding: "0 8px",
    margin: 0,
    backgroundColor: "transparent",
    color: "var(--colorNeutralForegroundInverted2)",
    fontSize: "17px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.01em",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "#3e4b56",
    },
    ":hover:active": {
      backgroundColor: "#2c353c",
    },
  },

  // Actions area (right side of header)
  headerActions: {
    gridArea: "actions",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },

  // Language selector button — wider than icon-only buttons to fit text
  headerLangBtn: {
    height: "var(--app-header-height, 48px)",
    padding: "0 12px",
    border: "0 solid transparent",
    borderRadius: 0,
    color: tokens.colorNeutralForegroundInverted2,
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    ":hover": {
      backgroundColor: "#3e4b56",
      color: tokens.colorNeutralForegroundInverted2,
    },
    ":hover:active": {
      backgroundColor: "#2c353c",
    },
  },
  // Icon-style header button (profile, theme toggle, etc.) — matches appHeaderBtn
  headerBtn: {
    minWidth: "var(--app-header-btn-width, 48px)",
    height: "var(--app-header-height, 48px)",
    padding: 0,
    margin: 0,
    border: "0 solid transparent",
    color: tokens.colorNeutralForegroundInverted2,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    ":hover": {
      backgroundColor: "#3e4b56",
      color: tokens.colorNeutralForegroundInverted2,
    },
    ":hover:active": {
      backgroundColor: "#2c353c",
    },
  },

  // Body below the fixed header
  body: {
    paddingTop: "calc(var(--app-header-height, 48px) + 1px)",
    flex: 1,
    display: "flex",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },

  // Sidebar navigation — matches ostroAppNavigation
  nav: {
    width: "250px",
    minWidth: "250px",
    flexShrink: "0",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground3,
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: tokens.colorNeutralStroke1,
    transitionProperty: "width, min-width",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease",
  },

  navCollapsed: {
    width: "56px",
    minWidth: "56px",
    // Hide label text while keeping icons visible
    "& .fui-Tab__content": {
      display: "none",
    },
    "& .fui-Tab": {
      justifyContent: "center",
      paddingLeft: "0",
      paddingRight: "0",
    },
  },

  // Main content area — matches ostroAppMain (no padding — views own their padding)
  main: {
    flex: 1,
    width: "100%",
    minWidth: 0,
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: tokens.colorNeutralBackground3,
  },

  // Centred fallback for loading / unauthenticated screens
  centred: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.5rem",
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

// ─── Component ────────────────────────────────────────────────────────────────

const DefaultLoadingFallback = (): React.JSX.Element => {
  const styles = useStyles();
  return (
    <div className={styles.centred}>
      <Spinner label="Loading ADAPT shell..." />
    </div>
  );
};

export const AuthenticatedLayout = ({
  children,
  appName,
  brand,
  externalLinks = DEFAULT_EXTERNAL_LINKS,
  headerActions,
  loadingFallback,
  navigation,
  navCollapsed = false,
  showThemeToggle = false,
  themeToggle,
  unauthenticatedFallback,
  showLanguageSelector = false,
  languages,
  defaultLanguage = "en",
  onLanguageChange,
}: AuthenticatedLayoutProps): React.JSX.Element => {
  const resolvedLanguages = languages ?? DEFAULT_LANGUAGES;
  const [selectedLang, setSelectedLang] = useState(defaultLanguage);
  const currentLangLabel =
    resolvedLanguages.find((l) => l.code === selectedLang)?.label ?? "English";
  const styles = useStyles();
  const { initialized, authenticated, user, login, logout } = useAuth();
  const adaptTheme = useOptionalAdaptTheme();
  const isDark = adaptTheme?.themeMode === "dark";
  const resolvedAppName = appName ?? getRuntimeConfig().applicationName;

  if (!initialized) {
    return <>{loadingFallback ?? <DefaultLoadingFallback />}</>;
  }

  // Log once when auth state resolves (initialized flips from false → true)
  perfMark("AUTH:resolved", { authenticated, user: user?.username ?? null });

  if (!authenticated) {
    return (
      <>
        {unauthenticatedFallback ?? (
          <div className={styles.centred}>
            <div style={{ maxWidth: "24rem", textAlign: "center" }}>
              <Text
                as="h1"
                size={500}
                weight="semibold"
                style={{ display: "block", marginBottom: "0.5rem" }}
              >
                {resolvedAppName}
              </Text>
              <Text
                as="p"
                style={{ display: "block", marginBottom: "1rem" }}
              >
                Sign in to continue.
              </Text>
              <Button appearance="primary" onClick={() => void login()}>
                Sign in
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Derive initials for profile avatar
  const initials = user?.displayName
    ? user.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <div className={styles.appShell}>
      {/* Fixed header — bg adapts: dark navy in light mode, body-match in dark mode */}
      <header
        className={styles.header}
        style={isDark ? {
          backgroundColor: tokens.colorNeutralBackground2,
          borderBottomColor: tokens.colorNeutralBackground5,
        } : undefined}
      >
        {/* Brand / app name — in dark mode use standard foreground, not inverted */}
        <div className={styles.headerBrand}>
          {brand ? (
            <span
              className={styles.brandBtn}
              style={isDark ? { color: tokens.colorNeutralForeground1 } : undefined}
            >
              {brand}
            </span>
          ) : (
            <span
              className={styles.brandBtn}
              style={isDark ? { color: tokens.colorNeutralForeground1 } : undefined}
            >
              {resolvedAppName}
            </span>
          )}
        </div>

        {/* Right-side actions */}
        <div className={styles.headerActions}>
          {headerActions}

          {/* External tooling links (MLflow, Evidently, OpenMetadata…) */}
          {externalLinks.length > 0 ? (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Tooltip content="External tools" relationship="label" appearance="inverted">
                  <Button
                    appearance="subtle"
                    className={styles.headerBtn}
                    icon={<LinkRegular />}
                    aria-label="External tools"
                    style={isDark ? { color: tokens.colorNeutralForeground1 } : undefined}
                  />
                </Tooltip>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {externalLinks.map((link) => (
                    <MenuItem
                      key={link.url}
                      onClick={() =>
                        window.open(link.url, "_blank", "noopener,noreferrer")
                      }
                    >
                      {link.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          ) : null}

          {/* Language selector */}
          {showLanguageSelector ? (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="subtle"
                  className={styles.headerLangBtn}
                  icon={<Globe20Regular />}
                  style={
                    isDark
                      ? { color: tokens.colorNeutralForeground1 }
                      : { color: tokens.colorNeutralForegroundInverted2 }
                  }
                >
                  {currentLangLabel}
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {resolvedLanguages.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      onClick={() => {
                        setSelectedLang(lang.code);
                        document.documentElement.lang = lang.code;
                        onLanguageChange?.(lang.code);
                      }}
                    >
                      {lang.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          ) : null}

          {/* Profile menu — shows name, theme toggle, sign out */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Tooltip
                content={user?.displayName ?? "Account"}
                relationship="label"
                appearance="inverted"
              >
                <Button
                  appearance="subtle"
                  className={styles.headerBtn}
                  aria-label={user?.displayName ?? "Account menu"}
                >
                  <Persona
                    size="medium"
                    avatar={{
                      initials,
                      color: "colorful",
                      style: { margin: 0 },
                    }}
                  />
                </Button>
              </Tooltip>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuGroup>
                  <MenuGroupHeader>
                    {user?.displayName ?? "Account"}
                  </MenuGroupHeader>
                  {/* Sun ☀️ │ toggle │ Moon 🌙 — matches legacy ProfileMenu layout */}
                  {themeToggle ??
                    (showThemeToggle ? (
                      <MenuItem
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: tokens.spacingHorizontalXS,
                        }}
                      >
                        <WeatherSunny20Regular
                          style={{
                            color: adaptTheme?.themeMode === "dark"
                              ? tokens.colorNeutralForeground3
                              : tokens.colorBrandForeground1,
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          onClick={() => adaptTheme?.setThemeMode("light")}
                        />
                        <Switch
                          checked={adaptTheme?.themeMode === "dark"}
                          onChange={(_, d) =>
                            adaptTheme?.setThemeMode(d.checked ? "dark" : "light")
                          }
                          style={{ margin: 0 }}
                        />
                        <WeatherMoon20Regular
                          style={{
                            color: adaptTheme?.themeMode === "dark"
                              ? tokens.colorBrandForeground1
                              : tokens.colorNeutralForeground3,
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          onClick={() => adaptTheme?.setThemeMode("dark")}
                        />
                      </MenuItem>
                    ) : null)}
                </MenuGroup>
                <MenuDivider />
                <MenuItem icon={<PersonRegular />}>
                  View Profile
                </MenuItem>
                <MenuItem
                  icon={<SignOutRegular />}
                  onClick={() => void logout()}
                >
                  Log off
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      {/* Body: sidebar + main */}
      <div className={styles.body}>
        {navigation ? (
          <aside
            className={mergeClasses(styles.nav, navCollapsed && styles.navCollapsed)}
            style={isDark ? { borderRightColor: tokens.colorNeutralBackground5 } : undefined}
          >
            {navigation}
          </aside>
        ) : null}
        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
};
